import qs from "qs";
import { getStrapiURL } from "@lib/strapi/content";

/**
 * C3: in-process TTL cache + inflight dedupe for CMS GETs. A production
 * build renders ~230 pages that each refetch the shared singletons (menus,
 * footer, cookie banner) and the recipe catalog — one process-wide entry per
 * URL collapses those. The TTL also batches ISR regenerations in prod.
 *
 * CMS_FETCH_CACHE_TTL_MS overrides (0 disables); defaults: 30s in
 * production, off in development so editors see changes instantly.
 * Cache hits return a structuredClone so callers can never mutate the
 * cached payload. The auth token is not part of the key — the server uses
 * a single read token.
 */
const TTL = (() => {
  const raw = process.env.CMS_FETCH_CACHE_TTL_MS;
  if (raw != null && raw !== "") {
    return Math.max(0, Number(raw) || 0);
  }
  return process.env.NODE_ENV === "production" ? 30_000 : 0;
})();

// the store lives on globalThis: Next bundles this module separately per
// entrypoint (API routes vs page renderers), and /api/revalidate must clear
// the SAME cache the regenerating pages read from
type FetchCacheStore = {
  cache: Map<string, { at: number; data: unknown }>;
  inflight: Map<string, Promise<unknown>>;
};
const store: FetchCacheStore = ((globalThis as any).__cmsFetchCache ??= {
  cache: new Map(),
  inflight: new Map(),
});
const { cache, inflight } = store;

// on-demand revalidation must re-read fresh CMS data — /api/revalidate calls
// this before regenerating pages
export const clearFetchCache = () => {
  cache.clear();
  inflight.clear();
};

export async function fetchAPI(path: string, query, authToken?: string) {
  const baseURL = getStrapiURL();
  const url = new URL(`/api/${path}`, baseURL);
  url.search = qs.stringify(query);
  const key = url.toString();

  const doFetch = async () => {
    const baseHeader = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    };

    const headerWithAuth = {
      ...baseHeader,
      headers: {
        ...baseHeader.headers,
        Authorization: `Bearer ${authToken}`,
      },
    };

    try {
      const response = await fetch(url, authToken ? headerWithAuth : baseHeader);
      if (!response.ok) {
        // non-2xx bodies may be proxy HTML error pages — never JSON.parse them
        throw new Error(`CMS request failed (HTTP ${response.status}) for ${url}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching data for", url.toString(), "-", error);
      throw error;
    }
  };

  if (TTL === 0) {
    return doFetch();
  }

  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL) {
    return structuredClone(hit.data);
  }

  const pending = inflight.get(key);
  if (pending) {
    return pending.then((data) => structuredClone(data));
  }

  const promise = doFetch()
    .then((data) => {
      cache.set(key, { at: Date.now(), data });
      inflight.delete(key);
      return data;
    })
    .catch((error) => {
      inflight.delete(key);
      throw error;
    });
  inflight.set(key, promise);
  return promise.then((data) => structuredClone(data));
}
